import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Typography,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  const { updateCredentials, username } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // 当对话框打开时，设置当前用户名
  useEffect(() => {
    if (open && username) {
      setNewUsername(username);
    }
  }, [open, username]);

  const handleSubmit = async () => {
    // 重置状态
    setError('');
    setSuccess(false);
    
    // 表单验证
    if (!newUsername) {
      setError('请输入用户名');
      return;
    }
    
    if (!currentPassword) {
      setError('请输入当前密码');
      return;
    }
    
    if (!newPassword) {
      setError('请输入新密码');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('新密码长度不能少于6个字符');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('两次输入的新密码不一致');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await updateCredentials(currentPassword, newPassword, newUsername);
      if (result.success) {
        setSuccess(true);
        // 成功后清空表单
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        // 2秒后自动关闭对话框
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError(result.message || '用户信息修改失败');
      }
    } catch (err) {
      setError('发生错误，请稍后重试');
      console.error('修改用户信息错误:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // 关闭对话框时重置状态
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess(false);
      // 重置用户名为当前用户名
      if (username) {
        setNewUsername(username);
      }
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>修改用户信息</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2, mt: 1 }}>
            用户信息修改成功！
          </Alert>
        )}
        
        <Typography variant="subtitle1" gutterBottom>
          用户信息
        </Typography>
        
        <TextField
          margin="dense"
          label="用户名"
          type="text"
          fullWidth
          variant="outlined"
          value={newUsername}
          onChange={(e) => setNewUsername(e.target.value)}
          disabled={loading}
          required
          sx={{ mb: 2 }}
        />
        
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          密码修改
        </Typography>
        
        <TextField
          label="当前密码"
          type={showCurrentPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          disabled={loading || success}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  edge="end"
                  disabled={loading || success}
                >
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="新密码"
          type={showNewPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading || success}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  disabled={loading || success}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="确认新密码"
          type={showConfirmPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading || success}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  disabled={loading || success}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>取消</Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained" 
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {loading ? '提交中...' : '保存修改'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;